import { Volume } from '~/shared/types/api';

/**
 * Stable identity for AG Grid rows and React keys. Prefer API `id` (UUID); fall back to `volumeId`.
 */
export const getVolumeStableRowId = (v: Pick<Volume, 'id' | 'volumeId'>): string =>
  (v.id && v.id.length > 0 ? v.id : v.volumeId) || 'unknown';

/**
 * Check if a volume can be datafied (turned on)
 */
export const canBeDatafied = (v: Volume) => v.isDatafiable;

/**
 * Check if a volume can be undatafied
 * A volume can be undatafied when:
 * - The backend flag isUnDatafiable is true
 * - AND the volume is in one of the allowed monitoring states
 *
 * Growing, Merging, and Detaching are deliberately excluded even though
 * `managedByStatus` below treats them as managed — they're active transitions where
 * undatafying mid-flight isn't supported. Datafied is included: unlike those, it's a
 * steady state (not a transition), so it should follow `isUnDatafiable` the same way
 * `Managed` does.
 */
export const canBeUnDatafied = (v: Volume) =>
  v.isUnDatafiable &&
  ['Managed', 'Migrating', 'Shrinking', 'Datafied'].includes(v?.monitoring || '');

/**
 * Whether each monitoring status describes a volume Datafy is managing.
 *
 * Spelled out as a full Record rather than an allowlist array so that adding a value
 * to `Volume['monitoring']` fails the build until it is classified here. That gap is
 * DT-11075: the SaaS status enum grew `Modifying`, the allowlist did not, and a managed
 * volume rendered as unmanaged for the hours an EBS modify spends optimizing.
 */
const managedByStatus: Record<Volume['monitoring'], boolean> = {
  Managed: true,
  Migrating: true,
  Growing: true,
  Shrinking: true,
  Merging: true,
  Detaching: true,
  // Transient: EBS is applying an iops/throughput change. The volume stays managed
  // throughout, and `awsStatus: optimizing` can run for hours.
  Modifying: true,
  Datafied: true,
  // Undatafy in flight. Deliberately false so the switch reads off the moment the user
  // asks for it, matching the optimistic 'Undatafying' label in OptimisticSwitch.
  Unmigrating: false,
  Monitored: false,
  Unmonitored: false,
  Unattached: false,
  Deleted: false,
  Failed: false,
  Unknown: false,
};

/**
 * Check if a volume's datafy switch is currently in the "on" state
 * This includes all managed states and transitional states.
 *
 * Accepts any object exposing `monitoring` so summary payloads (e.g. dry-run
 * `VolumeSummary`) work alongside full `Volume` objects.
 */
export const isSwitchedOn = (v: { monitoring?: Volume['monitoring'] }): boolean =>
  v?.monitoring ? managedByStatus[v.monitoring] === true : false;

/**
 * Check if the datafy switch should be disabled
 * The switch is disabled when:
 * - The volume cannot be datafied AND cannot be undatafied
 * - OR the volume is switched on but cannot be undatafied
 */
export const isSwitchDisabled = (v: Volume): boolean =>
  (!canBeDatafied(v) && !canBeUnDatafied(v)) || (isSwitchedOn(v) && !canBeUnDatafied(v));
